/**
 * Stage Finance — public engine API.
 *
 * Financial logic lives here. React views must consume ledger + selectors only.
 */

import { buildCashEvents, capitalCallsByMonth, resolveSaleEvents } from './buildCashEvents.js';
import { buildCostSchedule } from './buildCostSchedule.js';
import { buildLedger } from './buildLedger.js';
import { buildProgramme } from './buildProgramme.js';
import { buildSalesSchedule } from './buildSalesSchedule.js';
import { buildSalesPhaseTargets } from './salesPhases.js';
import {
  hydrateAssumptions,
  linkConstructionPaymentTranches,
  linkSalesAllocation,
  linkSaleMonthOffsets,
  linkLandAcquisitionMonth,
  programmeBoundsFromDurations,
  MODES,
  normalizeConstructionPaymentTranches,
  normalizeSalesAllocation,
} from './defaults.js';
import {
  expandQuotePartCashEvents,
  isQuotePartSaleCashEvent,
  isQuotePartSaleContextEvent,
  quotePartSaleSplitSummary,
  spvBuyerCashFromLedgerRow,
} from './expandQuotePartCashEvents.js';
import {
  actorFlowTotals,
  attachPositionsAfterEvents,
  compareCashEvents,
  EVENT_PHASE,
  eventPhase,
  materializeCashEventAudit,
  receivedActorBucket,
  sortCashEvents,
} from './cashEventAudit.js';
import { materializeCostSchedule, materializeSalesSchedule } from './materializeFromEvents.js';
import { normalizePnl } from './normalizePnl.js';
import {
  selectChartSeries,
  selectConclusions,
  selectStageRows,
} from './selectors.js';
import {
  isThreeSaleWindowFeasible,
  THREE_SALE_WINDOW_INCOMPLETE_REASON,
} from './timingControls.js';

export {
  buildCashEvents,
  capitalCallsByMonth,
  resolveSaleEvents,
  buildCostSchedule,
  buildLedger,
  buildProgramme,
  buildSalesSchedule,
  buildSalesPhaseTargets,
  expandQuotePartCashEvents,
  isQuotePartSaleCashEvent,
  isQuotePartSaleContextEvent,
  quotePartSaleSplitSummary,
  spvBuyerCashFromLedgerRow,
  actorFlowTotals,
  attachPositionsAfterEvents,
  compareCashEvents,
  EVENT_PHASE,
  eventPhase,
  materializeCashEventAudit,
  receivedActorBucket,
  sortCashEvents,
  hydrateAssumptions,
  materializeCostSchedule,
  materializeSalesSchedule,
  MODES,
  normalizePnl,
  linkConstructionPaymentTranches,
  linkSalesAllocation,
  linkSaleMonthOffsets,
  linkLandAcquisitionMonth,
  programmeBoundsFromDurations,
  isThreeSaleWindowFeasible,
  THREE_SALE_WINDOW_INCOMPLETE_REASON,
  normalizeConstructionPaymentTranches,
  normalizeSalesAllocation,
  selectChartSeries,
  selectConclusions,
  selectStageRows,
};

/**
 * Run the full Stage Finance engine for one structure/mode.
 *
 * @param {object} outputs P&L DealOutputs
 * @param {object} [rawAssumptions] Stage Finance assumptions
 * @returns {object} Engine result (ledger + selectors). No UI.
 */
export function runStageFinance(outputs, rawAssumptions = {}) {
  const assumptions = hydrateAssumptions(rawAssumptions);
  const pnl = normalizePnl(outputs);

  if (!pnl.complete) {
    return {
      incomplete: true,
      incompleteReasons: pnl.incompleteReasons,
      assumptions,
      pnl,
      programme: null,
      cashEvents: [],
      saleMode: null,
      costSchedule: null,
      salesSchedule: null,
      ledger: {
        incomplete: true,
        incompleteReasons: pnl.incompleteReasons,
        months: [],
        totals: {},
        facilityAnalysis: {},
      },
      conclusions: selectConclusions(null, pnl, assumptions),
      chart: { bars: [], line: [], milestones: [] },
      stages: [],
    };
  }

  const programme = buildProgramme(assumptions);
  // Three-sale assumption fallback needs ≥2 months after permit.
  // Full / partial price-list sales use real unit (+ residual) events — no milestone window.
  const { saleMode: resolvedSaleMode } = resolveSaleEvents(pnl, programme, assumptions);
  if (resolvedSaleMode === 'assumption' && !isThreeSaleWindowFeasible(programme)) {
    return {
      incomplete: true,
      incompleteReasons: [THREE_SALE_WINDOW_INCOMPLETE_REASON],
      assumptions,
      pnl,
      programme,
      cashEvents: [],
      saleMode: null,
      costSchedule: null,
      salesSchedule: null,
      ledger: {
        incomplete: true,
        incompleteReasons: [THREE_SALE_WINDOW_INCOMPLETE_REASON],
        months: [],
        totals: {},
        facilityAnalysis: {},
      },
      conclusions: selectConclusions(null, pnl, assumptions),
      chart: { bars: [], line: [], milestones: [] },
      stages: [],
    };
  }

  const { events: timingCashEvents, saleMode } = buildCashEvents(pnl, programme, assumptions);
  // Schedules / ledger consume timing events (gross sales). Display Cash events
  // for Quote-part are expanded after the ledger so party routes match reality.
  const costSchedule = materializeCostSchedule(timingCashEvents, programme);
  const salesSchedule = materializeSalesSchedule(timingCashEvents, pnl, programme, assumptions);
  const ledger = buildLedger({
    pnl,
    programme,
    costSchedule,
    salesSchedule,
    cashEvents: timingCashEvents,
    rawAssumptions: assumptions,
  });
  const cashEvents = materializeCashEventAudit(
    expandQuotePartCashEvents(timingCashEvents, ledger),
    ledger,
  );

  return {
    incomplete: false,
    assumptions,
    pnl,
    programme,
    cashEvents,
    saleMode,
    costSchedule,
    salesSchedule,
    ledger,
    conclusions: selectConclusions(ledger, pnl, assumptions),
    chart: selectChartSeries(ledger, salesSchedule),
    stages: selectStageRows(ledger),
    facilityAnalysis: ledger.facilityAnalysis,
  };
}

/** Run both structures for comparison when needed. */
export function runStageFinanceCompare(outputs, rawAssumptions = {}) {
  const base = hydrateAssumptions(rawAssumptions);
  return {
    venteATerme: runStageFinance(outputs, { ...base, mode: MODES.venteATerme }),
    quotePart: runStageFinance(outputs, { ...base, mode: MODES.quotePart }),
  };
}
